import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
	useGetClassByDepartmentQuery,
	useGetSubjectsQuery,
	useGetUserSubjectsQuery,
	useGetStudentsByDepartmentQuery,
} from "../../../store/api";
import { selectCurrentUser } from "../../../store/slices/authSlice";
import { getStudentDisplayName } from "../../../utils";

export const useGradesData = () => {
	const { departmentId } = useParams();
	const user = useSelector(selectCurrentUser);

	const {
		data: classData,
		isLoading: isLoadingClass,
		error: classError,
	} = useGetClassByDepartmentQuery(departmentId, {
		skip: !departmentId,
	});

	const { data: allSubjects = [], isLoading: isLoadingSubjects } =
		useGetSubjectsQuery();

	const { data: userSubjects = [], isLoading: isLoadingUserSubjects } =
		useGetUserSubjectsQuery();

	const { data: studentsData = [], isLoading: isLoadingStudents } =
		useGetStudentsByDepartmentQuery(departmentId, {
			skip: !departmentId,
		});

	const departmentSubjects = useMemo(() => {
		if (!allSubjects.length || !classData?.subjects) return [];

		const userSubjectsForDept =
			userSubjects
				?.filter((userSubj) => userSubj.departmentId?.includes(departmentId))
				?.map((userSubj) => userSubj.id) || [];

		return classData.subjects
			.map((subjectId) => {
				const subjectData = allSubjects.find((s) => s.id === subjectId);
				return {
					id: subjectId,
					name: subjectData?.name || `Subject ${subjectId.slice(-4)}`,
					teacherAssigned: userSubjectsForDept.includes(subjectId),
					class: classData.gradeLevel,
				};
			})
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [allSubjects, userSubjects, departmentId, classData]);

	const students = useMemo(() => {
		return studentsData
			.map((student) => ({
				id: student.id,
				name: getStudentDisplayName(student),
				studentId: `STU${(student.studentNumber || student.id)
					.slice(-4)
					.toUpperCase()}`,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [studentsData]);

	const isLoading =
		isLoadingClass ||
		isLoadingSubjects ||
		isLoadingUserSubjects ||
		isLoadingStudents;

	return {
		classData,
		isLoading,
		classError,
		departmentSubjects,
		students,
		user,
		departmentId,
	};
};
